import React, { useState, useEffect } from 'react';
import { useApi } from './DashboardLayout';
import { Database, Download, RefreshCw, CheckCircle, Clock, HardDrive, Table } from 'lucide-react';

export default function BackupSection() {
  const { apiFetch } = useApi();
  const [history, setHistory] = useState([]);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    const r = await apiFetch('/api/backup/history');
    if (r?.ok) { const d = await r.json(); setHistory(d.history || []); }
  };

  const handleCreate = async () => {
    setCreating(true); setMsg(''); setLastResult(null);
    const r = await apiFetch('/api/backup/create', { method: 'POST' });
    if (r?.ok) {
      const d = await r.json();
      setLastResult(d);
      setMsg(d.message);
      loadHistory();
    } else {
      const err = await r?.json().catch(() => null);
      setMsg(err?.detail || 'Fehler beim Erstellen des Backups');
    }
    setCreating(false);
  };

  const handleDownload = async () => {
    setDownloading(true);
    const token = localStorage.getItem('bgpr_token');
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await fetch(`${API}/api/backup/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download fehlgeschlagen');
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'bgpr_backup.sql';
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setMsg('Download fehlgeschlagen: ' + e.message); }
    setDownloading(false);
  };

  return (
    <>
      <div className="card" data-testid="backup-actions">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Database size={18} style={{ color: 'var(--primary)' }} />
          <span>Datensicherung</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>Erstellen Sie ein vollständiges SQL-Backup der MariaDB-Datenbank.</p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={creating} data-testid="backup-create-btn"
              style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
              {creating ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Database size={15} />}
              {creating ? 'Backup wird erstellt...' : 'Backup erstellen'}
            </button>
            <button className="btn" onClick={handleDownload} disabled={downloading} data-testid="backup-download-btn"
              style={{ padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border-color, #ddd)' }}>
              {downloading ? <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={15} />}
              {downloading ? 'Download läuft...' : 'SQL-Dump herunterladen'}
            </button>
          </div>

          {msg && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: lastResult ? '#D1FAE5' : '#FEF3C7', fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }} data-testid="backup-msg">
              {lastResult ? <CheckCircle size={16} color="#059669" /> : null}
              {msg}
            </div>
          )}

          {lastResult?.table_details && (
            <div data-testid="backup-details" style={{ background: 'var(--page-bg)', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HardDrive size={14} color="#6B7280" />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}><strong>{lastResult.database}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Table size={14} color="#6B7280" />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}><strong>{lastResult.tables}</strong> Tabellen</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}><strong>{lastResult.total_rows.toLocaleString('de-DE')}</strong> Datensätze</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px 16px' }}>
                {lastResult.table_details.map(t => (
                  <div key={t.table} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'monospace' }}>{t.table}</span>
                    <span style={{ fontWeight: 500 }}>{t.rows.toLocaleString('de-DE')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="card" data-testid="backup-history">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={18} style={{ color: 'var(--primary)' }} />
            <span>Backup-Historie</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color, #eee)' }}>
                    <th style={thS}>Datum</th>
                    <th style={thS}>Erstellt von</th>
                    <th style={thS}>Datenbank</th>
                    <th style={{ ...thS, textAlign: 'right' }}>Tabellen</th>
                    <th style={{ ...thS, textAlign: 'right' }}>Datensätze</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} style={{ borderBottom: '1px solid var(--border-color, #f0f0f0)' }}>
                      <td style={tdS}>{new Date(h.created_at).toLocaleString('de-DE')}</td>
                      <td style={tdS}>{h.created_by}</td>
                      <td style={{ ...tdS, fontFamily: 'monospace' }}>{h.database}</td>
                      <td style={{ ...tdS, textAlign: 'right' }}>{h.tables}</td>
                      <td style={{ ...tdS, textAlign: 'right' }}>{h.total_rows?.toLocaleString('de-DE')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const thS = { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#6B7280', whiteSpace: 'nowrap' };
const tdS = { padding: '10px 12px', verticalAlign: 'middle' };
